import { isDefined } from "@clipboard-health/util-ts";
import {
  type ExtraWorkedTimeRequestEntity,
  ExtraWorkedTimeRequestStatus,
} from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import { type WorkerShift } from "@src/appV2/Shifts/Shift/types";
import { parseISO } from "date-fns";
import { useMemo } from "react";

import { useGetWorkerShift } from "../../api/useGetWorkerShift";
import { type MissedPunchRequestStatus } from "../types";
import { useGetExtraWorkedTimeRequestsByShift } from "./useGetExtraWorkedTimeRequestsByShift";

interface PastShiftDetailsData {
  shift: WorkerShift;
  missedPunchRequestStatus: MissedPunchRequestStatus;
  isFullShiftPayment: boolean;
  requestedAmountInDollars?: number;
  totalPayInDollars: number;
  hourlyRateInDollars: number;
}

interface PastShiftDetailsParams {
  shiftId: string;
}

interface PastShiftDetailsResult {
  data: PastShiftDetailsData | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function useGetPastShiftDetailsData(params: PastShiftDetailsParams): PastShiftDetailsResult {
  const { shiftId } = params;

  const isEnabled = isDefined(shiftId) && shiftId.length > 0;

  const {
    data: shiftData,
    isLoading: isShiftLoading,
    isError: isShiftError,
  } = useGetWorkerShift(shiftId, {
    enabled: isEnabled,
  });

  const {
    data: extraWorkedTimeRequestsData,
    isLoading: isExtraWorkedTimeRequestsLoading,
    isError: isExtraWorkedTimeRequestsError,
  } = useGetExtraWorkedTimeRequestsByShift(shiftId, {
    enabled: isEnabled,
  });

  const shift = shiftData?.response;

  const pastShiftDetailsData = useMemo((): PastShiftDetailsData | undefined => {
    if (!isDefined(shift)) {
      return undefined;
    }

    const relevantRequest = getLatestActionableRequest(extraWorkedTimeRequestsData?.data ?? []);

    const hourlyRateInDollars = shift.offer?.pay.value ?? shift.finalPay ?? 0;
    const totalPayInDollars = shift.originalAmount ?? 0;

    return {
      shift,
      missedPunchRequestStatus: getMissedPunchRequestStatus(relevantRequest?.attributes.status),
      isFullShiftPayment: relevantRequest?.attributes.isFullShiftPayment ?? false,
      requestedAmountInDollars: isDefined(relevantRequest)
        ? ((relevantRequest.attributes.timeRequestedInMinutes ?? 0) / 60) * hourlyRateInDollars
        : undefined,
      totalPayInDollars,
      hourlyRateInDollars,
    };
  }, [shift, extraWorkedTimeRequestsData]);

  return {
    data: pastShiftDetailsData,
    isLoading: isShiftLoading || isExtraWorkedTimeRequestsLoading,
    isError: isShiftError || isExtraWorkedTimeRequestsError,
  };
}

function getMissedPunchRequestStatus(
  status?: ExtraWorkedTimeRequestStatus
): MissedPunchRequestStatus {
  if (!isDefined(status)) {
    return "none";
  }

  if (status === ExtraWorkedTimeRequestStatus.DECLINED) {
    return "declined";
  }

  if (status === ExtraWorkedTimeRequestStatus.PENDING) {
    return "pending";
  }

  return "none";
}

/**
 * Returns the most recent request by createdAt, or undefined if there are
 * no requests or the latest request has been approved (i.e., fully resolved).
 */
function getLatestActionableRequest(
  requests: ExtraWorkedTimeRequestEntity[]
): ExtraWorkedTimeRequestEntity | undefined {
  const [latest] = requests.toSorted(
    (a, b) =>
      parseISO(b.attributes.createdAt).getTime() - parseISO(a.attributes.createdAt).getTime()
  );

  if (latest?.attributes.status === ExtraWorkedTimeRequestStatus.APPROVED) {
    return undefined;
  }

  return latest;
}
